import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  X, Loader2, BarChart3, Eye, Users, DollarSign, Clock,
  Monitor, Smartphone, Tablet, Globe, TrendingUp, ArrowUpRight,
  Code2, MousePointerClick, Target, ChevronDown, ChevronRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventAnalytics({ eventId, eventName, onClose, embedded = false }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [widget, setWidget] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
    fetchWidget();
  }, [eventId]);

  const fetchAnalytics = async () => {
    try {
      const res = await axios.get(`${API}/events/${eventId}/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchWidget = async () => {
    // Widget analytics is owner-only; fail-soft so non-owners
    // (e.g. promo-partner viewers) don't see a toast spam.
    try {
      const res = await axios.get(`${API}/events/${eventId}/widget-analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWidget(res.data);
    } catch (_) {
      setWidget(null);
    }
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  };

  const sourceColors = {
    direct: 'bg-blue-500',
    search: 'bg-emerald-500',
    social: 'bg-purple-500',
    email: 'bg-amber-500',
    referral: 'bg-rose-500',
    internal: 'bg-indigo-500',
  };

  const sourceLabels = {
    direct: 'Direct',
    search: 'Search',
    social: 'Social Media',
    email: 'Email',
    referral: 'Referral',
    internal: 'Internal',
  };

  const deviceIcons = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

  if (loading) {
    if (embedded) {
      return (
        <div className="py-8 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-400" />
        </div>
      );
    }
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!data) return null;
  const o = data.overview;

  if (embedded) {
    return (
      <div className="space-y-5" data-testid="event-analytics-embedded">
        <div className="p-5 space-y-5">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="analytics-kpis">
            <KPICard icon={Eye} label="Total Views" value={o.total_views} color="indigo" />
            <KPICard icon={Users} label="Unique Visitors" value={o.unique_visitors} color="blue" />
            <KPICard icon={TrendingUp} label="Conversion Rate" value={`${o.conversion_rate}%`} color="emerald" />
            <KPICard icon={DollarSign} label="Revenue/Visitor" value={`$${o.revenue_per_visitor}`} color="amber" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <KPICard icon={Clock} label="Avg. Time on Page" value={formatDuration(o.avg_session_duration)} color="purple" />
            <KPICard icon={ArrowUpRight} label="Sell-Through" value={`${o.sell_through_rate}%`} color="rose" />
            <KPICard icon={DollarSign} label="Total Revenue" value={`$${o.total_revenue.toLocaleString()}`} color="emerald" />
            <KPICard icon={Users} label="Tickets Sold" value={`${o.total_tickets_sold}/${o.total_capacity}`} color="blue" />
            <KPICard icon={Target} label="Add-ons Sold" value={o.total_addons_sold ?? 0} color="amber" />
          </div>

          {/* Views Over Time */}
          <Card className="border-border">
            <CardContent className="p-4">
              <ViewsOverTime daily={data.daily_views} />
            </CardContent>
          </Card>

          <div className="grid sm:grid-cols-2 gap-4">
            <Card className="border-border">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-3">Traffic Sources</h3>
                {Object.keys(data.sources).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No data yet</p>
                ) : (
                  <div className="space-y-2" data-testid="traffic-sources">
                    {Object.entries(data.sources).sort((a, b) => b[1] - a[1]).map(([source, count]) => {
                      const total = Object.values(data.sources).reduce((s, v) => s + v, 0);
                      const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                      return (
                        <div key={source} className="flex items-center gap-2">
                          <div className={`w-2.5 h-2.5 rounded-full ${sourceColors[source] || 'bg-gray-500'}`} />
                          <span className="text-xs flex-1">{sourceLabels[source] || source}</span>
                          <span className="text-xs font-medium">{count}</span>
                          <span className="text-[10px] text-muted-foreground w-8 text-right">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-3">Devices</h3>
                {Object.keys(data.devices).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No data yet</p>
                ) : (
                  <div className="space-y-3" data-testid="device-breakdown">
                    {Object.entries(data.devices).sort((a, b) => b[1] - a[1]).map(([device, count]) => {
                      const total = Object.values(data.devices).reduce((s, v) => s + v, 0);
                      const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                      const Icon = deviceIcons[device] || Monitor;
                      return (
                        <div key={device} className="flex items-center gap-3">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-xs capitalize flex-1">{device}</span>
                          <div className="w-24 h-1.5 bg-muted/40 rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-xs font-medium w-8 text-right">{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Card className="border-border">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" /> Top States
                </h3>
                {data.geographic.states.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No geographic data yet</p>
                ) : (
                  <div className="space-y-1.5" data-testid="geo-states">
                    {data.geographic.states.map((s, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xs w-4 text-muted-foreground">{i + 1}.</span>
                        <span className="text-xs flex-1">{s.state}</span>
                        <Badge variant="outline" className="text-[10px]">{s.views}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="border-border">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" /> Top Cities
                </h3>
                {data.geographic.cities.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No geographic data yet</p>
                ) : (
                  <div className="space-y-1.5" data-testid="geo-cities">
                    {data.geographic.cities.map((c, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xs w-4 text-muted-foreground">{i + 1}.</span>
                        <span className="text-xs flex-1">{c.city}</span>
                        <Badge variant="outline" className="text-[10px]">{c.views}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Card className="border-border">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-3">Top Referrers</h3>
                {data.top_referrers.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No referrer data yet</p>
                ) : (
                  <div className="space-y-1.5" data-testid="top-referrers">
                    {data.top_referrers.map((r, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xs flex-1 truncate">{r.domain}</span>
                        <Badge variant="outline" className="text-[10px]">{r.views}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-3">Peak Viewing Hours</h3>
                {data.peak_hours.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No data yet</p>
                ) : (
                  <div className="space-y-1.5" data-testid="peak-hours">
                    {data.peak_hours.map((h, i) => {
                      const hour = parseInt(h.hour);
                      const label = hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`;
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-xs w-14">{label}</span>
                          <div className="flex-1 h-1.5 bg-muted/40 rounded-full overflow-hidden">
                            <div className="h-full bg-amber-500 rounded-full" style={{ width: `${(h.views / data.peak_hours[0].views) * 100}%` }} />
                          </div>
                          <span className="text-xs font-medium w-8 text-right">{h.views}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <WidgetPerformance widget={widget} />

          <AttributionSection promoters={data.promoters} editorial={data.editorial} />

          {o.total_views === 0 && (
            <div className="text-center py-8">
              <BarChart3 className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No views yet</p>
              <p className="text-xs text-muted-foreground mt-1">Analytics will appear once visitors view this event page</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center z-50 p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-4xl my-8 bg-card rounded-xl border border-border shadow-2xl" onClick={e => e.stopPropagation()} data-testid="event-analytics-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
              <BarChart3 className="h-5 w-5 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Event Analytics</h2>
              <p className="text-xs text-muted-foreground">{eventName}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-analytics">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-5 space-y-5">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="analytics-kpis">
            <KPICard icon={Eye} label="Total Views" value={o.total_views} color="indigo" />
            <KPICard icon={Users} label="Unique Visitors" value={o.unique_visitors} color="blue" />
            <KPICard icon={TrendingUp} label="Conversion Rate" value={`${o.conversion_rate}%`} color="emerald" />
            <KPICard icon={DollarSign} label="Revenue/Visitor" value={`$${o.revenue_per_visitor}`} color="amber" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <KPICard icon={Clock} label="Avg. Time on Page" value={formatDuration(o.avg_session_duration)} color="purple" />
            <KPICard icon={ArrowUpRight} label="Sell-Through" value={`${o.sell_through_rate}%`} color="rose" />
            <KPICard icon={DollarSign} label="Total Revenue" value={`$${o.total_revenue.toLocaleString()}`} color="emerald" />
            <KPICard icon={Users} label="Tickets Sold" value={`${o.total_tickets_sold}/${o.total_capacity}`} color="blue" />
            <KPICard icon={Target} label="Add-ons Sold" value={o.total_addons_sold ?? 0} color="amber" />
          </div>

          {/* Views Over Time */}
          <Card className="border-border">
            <CardContent className="p-4">
              <ViewsOverTime daily={data.daily_views} />
            </CardContent>
          </Card>

          <div className="grid sm:grid-cols-2 gap-4">
            {/* Traffic Sources */}
            <Card className="border-border">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-3">Traffic Sources</h3>
                {Object.keys(data.sources).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No data yet</p>
                ) : (
                  <div className="space-y-2" data-testid="traffic-sources">
                    {Object.entries(data.sources)
                      .sort((a, b) => b[1] - a[1])
                      .map(([source, count]) => {
                        const total = Object.values(data.sources).reduce((s, v) => s + v, 0);
                        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                        return (
                          <div key={source} className="flex items-center gap-2">
                            <div className={`w-2.5 h-2.5 rounded-full ${sourceColors[source] || 'bg-gray-500'}`} />
                            <span className="text-xs flex-1">{sourceLabels[source] || source}</span>
                            <span className="text-xs font-medium">{count}</span>
                            <span className="text-[10px] text-muted-foreground w-8 text-right">{pct}%</span>
                          </div>
                        );
                      })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Device Breakdown */}
            <Card className="border-border">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-3">Devices</h3>
                {Object.keys(data.devices).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No data yet</p>
                ) : (
                  <div className="space-y-3" data-testid="device-breakdown">
                    {Object.entries(data.devices)
                      .sort((a, b) => b[1] - a[1])
                      .map(([device, count]) => {
                        const total = Object.values(data.devices).reduce((s, v) => s + v, 0);
                        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                        const Icon = deviceIcons[device] || Monitor;
                        return (
                          <div key={device} className="flex items-center gap-3">
                            <Icon className="h-4 w-4 text-muted-foreground" />
                            <span className="text-xs capitalize flex-1">{device}</span>
                            <div className="w-24 h-1.5 bg-muted/40 rounded-full overflow-hidden">
                              <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs font-medium w-8 text-right">{pct}%</span>
                          </div>
                        );
                      })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {/* Geographic - States */}
            <Card className="border-border">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" /> Top States
                </h3>
                {data.geographic.states.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No geographic data yet</p>
                ) : (
                  <div className="space-y-1.5" data-testid="geo-states">
                    {data.geographic.states.map((s, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xs w-4 text-muted-foreground">{i + 1}.</span>
                        <span className="text-xs flex-1">{s.state}</span>
                        <Badge variant="outline" className="text-[10px]">{s.views}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Geographic - Cities */}
            <Card className="border-border">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Globe className="h-4 w-4 text-muted-foreground" /> Top Cities
                </h3>
                {data.geographic.cities.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No geographic data yet</p>
                ) : (
                  <div className="space-y-1.5" data-testid="geo-cities">
                    {data.geographic.cities.map((c, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xs w-4 text-muted-foreground">{i + 1}.</span>
                        <span className="text-xs flex-1">{c.city}</span>
                        <Badge variant="outline" className="text-[10px]">{c.views}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Top Referrers & Peak Hours */}
          <div className="grid sm:grid-cols-2 gap-4">
            <Card className="border-border">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-3">Top Referrers</h3>
                {data.top_referrers.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No referrer data yet</p>
                ) : (
                  <div className="space-y-1.5" data-testid="top-referrers">
                    {data.top_referrers.map((r, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span className="text-xs flex-1 truncate">{r.domain}</span>
                        <Badge variant="outline" className="text-[10px]">{r.views}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-border">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-3">Peak Viewing Hours</h3>
                {data.peak_hours.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No data yet</p>
                ) : (
                  <div className="space-y-1.5" data-testid="peak-hours">
                    {data.peak_hours.map((h, i) => {
                      const hour = parseInt(h.hour);
                      const label = hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`;
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-xs w-14">{label}</span>
                          <div className="flex-1 h-1.5 bg-muted/40 rounded-full overflow-hidden">
                            <div className="h-full bg-amber-500 rounded-full" style={{ width: `${(h.views / data.peak_hours[0].views) * 100}%` }} />
                          </div>
                          <span className="text-xs font-medium w-8 text-right">{h.views}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <WidgetPerformance widget={widget} />

          <AttributionSection promoters={data.promoters} editorial={data.editorial} />

          {/* Empty State */}
          {o.total_views === 0 && (
            <div className="text-center py-8">
              <BarChart3 className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No views yet</p>
              <p className="text-xs text-muted-foreground mt-1">Analytics will appear once visitors view this event page</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AttributionSection({ promoters = [], editorial = [] }) {
  if (promoters.length === 0 && editorial.length === 0) return null;
  const usd = (c) => `$${((c || 0) / 100).toFixed(2)}`;
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="attribution-section">
      {promoters.length > 0 && (
        <Card className="border-border">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold mb-1">Promoters</h3>
            <p className="text-[10px] text-muted-foreground mb-3">Street-team partners driving traffic & sales for this event</p>
            <div className="overflow-x-auto" data-testid="analytics-promoters">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase text-muted-foreground border-b border-border/60">
                    <th className="text-left py-1.5 pr-2 font-semibold">Promoter</th>
                    <th className="text-right py-1.5 px-1.5 font-semibold">Impr</th>
                    <th className="text-right py-1.5 px-1.5 font-semibold">Clicks</th>
                    <th className="text-right py-1.5 px-1.5 font-semibold">CTR</th>
                    <th className="text-right py-1.5 px-1.5 font-semibold">Sales</th>
                    <th className="text-right py-1.5 px-1.5 font-semibold">Tickets</th>
                    <th className="text-right py-1.5 px-1.5 font-semibold">Add-ons</th>
                    <th className="text-right py-1.5 px-1.5 font-semibold">Revenue</th>
                    <th className="text-right py-1.5 pl-1.5 font-semibold">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {promoters.map((p, i) => (
                    <tr key={i} className="border-b border-border/40 last:border-0" data-testid={`analytics-promoter-${i}`}>
                      <td className="py-1.5 pr-2 font-medium max-w-[140px] truncate">
                        {p.handle ? <a href={`/promo/${p.handle}`} target="_blank" rel="noreferrer" className="hover:underline">{p.name}</a> : p.name}
                        {p.status !== 'active' && <Badge variant="outline" className="ml-1.5 text-[9px] text-rose-400">inactive</Badge>}
                      </td>
                      <td className="text-right py-1.5 px-1.5 text-muted-foreground">{p.impressions ?? 0}</td>
                      <td className="text-right py-1.5 px-1.5">{p.clicks ?? 0}</td>
                      <td className="text-right py-1.5 px-1.5 text-muted-foreground">{(p.ctr ?? 0)}%</td>
                      <td className="text-right py-1.5 px-1.5 font-medium">{p.sales ?? 0}</td>
                      <td className="text-right py-1.5 px-1.5">{p.tickets ?? 0}</td>
                      <td className="text-right py-1.5 px-1.5">{p.addons ?? 0}</td>
                      <td className="text-right py-1.5 px-1.5 font-semibold">{usd(p.revenue_cents)}</td>
                      <td className="text-right py-1.5 pl-1.5 text-emerald-400 font-semibold">{usd(p.commission_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      {editorial.length > 0 && (
        <Card className="border-border">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold mb-1">Eventism Editorial</h3>
            <p className="text-[10px] text-muted-foreground mb-3">Articles & guides sending readers to this event</p>
            <div className="space-y-2" data-testid="analytics-editorial">
              {editorial.map((e, i) => (
                <div key={i} className="text-xs border-b border-border/40 pb-2 last:border-0" data-testid={`analytics-editorial-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium truncate flex-1">
                      <a href={e.kind === 'blog' ? `/blog/${e.slug}` : `/denver/guides/${e.slug}`} target="_blank" rel="noreferrer" className="hover:underline">{e.title}</a>
                    </p>
                    <Badge variant="outline" className="text-[9px] flex-shrink-0">{e.kind === 'blog' ? 'Blog' : 'Guide'}</Badge>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    by {e.author} · {e.views} view{e.views === 1 ? '' : 's'} · {e.orders} order{e.orders === 1 ? '' : 's'} · {e.tickets} ticket{e.tickets === 1 ? '' : 's'} · <span className="text-emerald-400 font-semibold">${(e.revenue || 0).toFixed(2)}</span>
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function KPICard({ icon: Icon, label, value, color }) {
  const colorMap = {
    indigo: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
    rose: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
  };
  const classes = colorMap[color] || colorMap.indigo;

  return (
    <div className={`p-3 rounded-lg border ${classes}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[10px] opacity-80">{label}</span>
      </div>
      <p className="text-lg font-bold">{value}</p>
    </div>
  );
}


/**
 * Renders the "Views Over Time" daily chart.
 *
 * The backend `daily_views` array only contains days that actually had
 * views; we pad it to the trailing 14 days so the chart always shows a
 * full week-by-day visual — even on brand-new events the user gets a
 * "skeleton" line of zeroed bars with day labels, instead of an empty
 * text placeholder.
 */
function ViewsOverTime({ daily }) {
  const DAYS = 14;
  const today = new Date();
  // Build a Date[] for the trailing DAYS days, oldest first.
  const dates = [];
  for (let i = DAYS - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    dates.push(d);
  }
  const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const lookup = {};
  (daily || []).forEach((row) => { lookup[row.date] = row.views; });
  const series = dates.map((d) => ({
    iso: toISO(d),
    weekday: d.toLocaleDateString(undefined, { weekday: 'short' }),
    monthDay: d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    views: lookup[toISO(d)] || 0,
  }));
  const total = series.reduce((s, r) => s + r.views, 0);
  const max = Math.max(1, ...series.map((s) => s.views));

  return (
    <div data-testid="views-over-time">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="text-sm font-semibold">Views Over Time</h3>
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Last {DAYS} days · <span className="text-foreground font-semibold tabular-nums">{total.toLocaleString()}</span> views
        </p>
      </div>
      <div className="flex items-end gap-1 h-32" data-testid="views-chart">
        {series.map((s, i) => {
          const isEmpty = s.views === 0;
          const heightPct = isEmpty ? 6 : Math.max(8, Math.round((s.views / max) * 100));
          return (
            <div key={s.iso} className="flex-1 flex flex-col justify-end items-center gap-1 group relative h-full">
              <div className="w-full flex-1 flex items-end">
                <div
                  className={
                    isEmpty
                      ? 'w-full rounded-t bg-indigo-500/15 border-t border-indigo-500/30 transition-colors'
                      : 'w-full rounded-t bg-gradient-to-t from-indigo-500/80 to-fuchsia-500/80 hover:from-indigo-400 hover:to-fuchsia-400 transition-colors cursor-default'
                  }
                  style={{ height: `${heightPct}%` }}
                />
              </div>
              <span className="text-[9px] text-muted-foreground truncate w-full text-center leading-tight">
                {s.weekday}
              </span>
              <span className="text-[8px] text-muted-foreground/60 truncate w-full text-center leading-tight">
                {s.monthDay}
              </span>
              <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-foreground text-background text-[10px] px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap shadow-sm">
                {s.views} {s.views === 1 ? 'view' : 'views'} · {s.monthDay}
              </div>
              {/* hidden day-index so tests can introspect */}
              <span className="sr-only" data-testid={`views-bar-${i}`}>{s.views}</span>
            </div>
          );
        })}
      </div>
      {total === 0 && (
        <p className="mt-3 text-[11px] text-muted-foreground text-center">
          No views yet — bars will fill in as visitors arrive.
        </p>
      )}
    </div>
  );
}



/**
 * WidgetPerformance — renders the "Embeddable Widget Performance" block on
 * the EventAnalytics dashboard. Shows totals (impressions / clicks / CTR /
 * conversions / revenue), a per-host breakdown table, and a 7-day
 * impressions sparkline. Renders nothing for non-owners (widget === null)
 * or events that have never been embedded (totals are all 0).
 */
function WidgetPerformance({ widget }) {
  if (!widget) return null;
  const t = widget.totals || {};
  const hosts = widget.hosts || [];
  const sources = widget.traffic_sources || [];
  const daily = widget.daily_impressions || [];
  const hasAny =
    (t.impressions || 0) + (t.clicks || 0) + (t.conversions || 0) > 0;

  return (
    <Card className="border-border" data-testid="widget-performance">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-fuchsia-500/10 flex items-center justify-center">
              <Code2 className="h-3.5 w-3.5 text-fuchsia-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">Embeddable Widget Performance</h3>
              <p className="text-[11px] text-muted-foreground">
                Sales driven by your embed on partner sites
              </p>
            </div>
          </div>
          {hasAny && (
            <Badge variant="outline" className="text-[10px]" data-testid="widget-hosts-count">
              {hosts.length} {hosts.length === 1 ? 'host' : 'hosts'}
            </Badge>
          )}
        </div>

        {!hasAny ? (
          <div className="text-center py-6 border border-dashed border-border rounded-lg" data-testid="widget-empty">
            <Code2 className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">
              Embed the ticket widget on your or a partner site to track impressions, clicks, and sales here.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="widget-kpis">
              <KPICard icon={Eye} label="Impressions" value={(t.impressions || 0).toLocaleString()} color="indigo" />
              <KPICard icon={MousePointerClick} label="Clicks" value={(t.clicks || 0).toLocaleString()} color="blue" />
              <KPICard icon={TrendingUp} label="CTR" value={`${t.ctr || 0}%`} color="purple" />
              <KPICard icon={Target} label="Conversions" value={(t.conversions || 0).toLocaleString()} color="emerald" />
              <KPICard icon={Target} label="Add-ons" value={(t.addons || 0).toLocaleString()} color="rose" />
              <KPICard icon={DollarSign} label="Revenue" value={`$${(t.revenue || 0).toLocaleString()}`} color="amber" />
            </div>

            {hosts.length > 0 && (
              <div className="overflow-x-auto -mx-1" data-testid="widget-hosts-table">
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    <tr className="border-b border-border">
                      <th className="text-left font-medium px-2 py-2">Host</th>
                      <th className="text-right font-medium px-2 py-2">Impr.</th>
                      <th className="text-right font-medium px-2 py-2">Clicks</th>
                      <th className="text-right font-medium px-2 py-2">CTR</th>
                      <th className="text-right font-medium px-2 py-2">Sales</th>
                      <th className="text-right font-medium px-2 py-2">Tickets</th>
                      <th className="text-right font-medium px-2 py-2">Add-ons</th>
                      <th className="text-right font-medium px-2 py-2">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hosts.map((h) => (
                      <HostRows key={h.host} host={h} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {sources.length > 0 && (
              <div data-testid="widget-traffic-sources">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">
                  Traffic sources — where visitors arrive on the host page from
                </p>
                <div className="overflow-x-auto -mx-1">
                  <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-wider text-muted-foreground">
                      <tr className="border-b border-border">
                        <th className="text-left font-medium px-2 py-2">Source</th>
                        <th className="text-right font-medium px-2 py-2">Impr.</th>
                        <th className="text-right font-medium px-2 py-2">Clicks</th>
                        <th className="text-right font-medium px-2 py-2">Sales</th>
                        <th className="text-right font-medium px-2 py-2">Tickets</th>
                        <th className="text-right font-medium px-2 py-2">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sources.map((s) => (
                        <tr key={s.source} className="border-b border-border/40 hover:bg-muted/20" data-testid={`widget-source-row-${s.source}`}>
                          <td className="px-2 py-2 font-medium capitalize">{s.source === 'direct' ? 'Direct / unknown' : s.source}</td>
                          <td className="px-2 py-2 text-right text-muted-foreground">{(s.impressions || 0).toLocaleString()}</td>
                          <td className="px-2 py-2 text-right text-muted-foreground">{(s.clicks || 0).toLocaleString()}</td>
                          <td className="px-2 py-2 text-right">{(s.conversions || 0).toLocaleString()}</td>
                          <td className="px-2 py-2 text-right text-muted-foreground">{(s.tickets || 0).toLocaleString()}</td>
                          <td className="px-2 py-2 text-right font-semibold text-emerald-400">${(s.revenue || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1.5">
                  Full source detail (Instagram, Google, Facebook ads…) requires the latest embed snippet — re-copy it from Event Marketing → Embed Widget.
                </p>
              </div>
            )}

            {daily.length > 0 && (
              <WidgetSparkline daily={daily} />
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * HostRows — renders one host row plus an expandable cluster of
 * campaign sub-rows when UTM data is present. Hosts without any UTM
 * traffic render as a single row (no toggle), so partners who never
 * tag their links still get a clean table.
 */
function HostRows({ host: h }) {
  const campaigns = h.campaigns || [];
  const hasCampaigns = campaigns.length > 0;
  const [open, setOpen] = useState(true);

  return (
    <>
      <tr
        className={`border-b border-border/40 hover:bg-muted/20 ${hasCampaigns ? 'cursor-pointer' : ''}`}
        onClick={() => hasCampaigns && setOpen((v) => !v)}
        data-testid={`widget-host-row-${h.host}`}
      >
        <td className="px-2 py-2 truncate max-w-[200px]">
          <div className="flex items-center gap-1.5">
            {hasCampaigns ? (
              open
                ? <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
                : <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
            ) : (
              <Globe className="h-3 w-3 text-muted-foreground shrink-0" />
            )}
            <span className="truncate">{h.host || 'direct'}</span>
            {hasCampaigns && (
              <Badge variant="outline" className="text-[9px] h-4 px-1.5">
                {campaigns.length}
              </Badge>
            )}
          </div>
        </td>
        <td className="px-2 py-2 text-right tabular-nums">{(h.impressions || 0).toLocaleString()}</td>
        <td className="px-2 py-2 text-right tabular-nums">{(h.clicks || 0).toLocaleString()}</td>
        <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">{h.ctr || 0}%</td>
        <td className="px-2 py-2 text-right tabular-nums">{(h.conversions || 0).toLocaleString()}</td>
        <td className="px-2 py-2 text-right tabular-nums">{(h.tickets || 0).toLocaleString()}</td>
        <td className="px-2 py-2 text-right tabular-nums">{(h.addons || 0).toLocaleString()}</td>
        <td className="px-2 py-2 text-right tabular-nums font-medium text-emerald-400">
          ${(h.revenue || 0).toLocaleString()}
        </td>
      </tr>
      {hasCampaigns && open && campaigns.map((c, i) => {
        const label = [c.utm_source, c.utm_medium, c.utm_campaign].filter(Boolean).join(' / ') || 'untagged';
        return (
          <tr
            key={`${h.host}-c-${i}`}
            className="border-b border-border/20 bg-muted/10"
            data-testid={`widget-campaign-row-${h.host}-${i}`}
          >
            <td className="px-2 py-1.5 pl-8 truncate max-w-[260px]">
              <div className="flex items-center gap-1.5">
                <span className="text-muted-foreground/60">↳</span>
                <span className="truncate text-muted-foreground" title={label}>{label}</span>
              </div>
            </td>
            <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">{(c.impressions || 0).toLocaleString()}</td>
            <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">{(c.clicks || 0).toLocaleString()}</td>
            <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground/70">{c.ctr || 0}%</td>
            <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">{(c.conversions || 0).toLocaleString()}</td>
            <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">{(c.tickets || 0).toLocaleString()}</td>
            <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">{(c.addons || 0).toLocaleString()}</td>
            <td className="px-2 py-1.5 text-right tabular-nums text-emerald-400/90">
              ${(c.revenue || 0).toLocaleString()}
            </td>
          </tr>
        );
      })}
    </>
  );
}


/**
 * 7-day impression sparkline. The backend only returns days that had
 * impressions, so we pad to the trailing 7 days so the chart is always
 * the same width.
 */
function WidgetSparkline({ daily }) {
  const DAYS = 7;
  const today = new Date();
  const series = [];
  const toISO = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const lookup = {};
  daily.forEach((r) => { lookup[r.date] = r.impressions; });
  for (let i = DAYS - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    series.push({
      iso: toISO(d),
      weekday: d.toLocaleDateString(undefined, { weekday: 'short' }),
      impressions: lookup[toISO(d)] || 0,
    });
  }
  const max = Math.max(1, ...series.map((s) => s.impressions));

  return (
    <div data-testid="widget-sparkline">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">
        Impressions &middot; last 7 days
      </p>
      <div className="flex items-end gap-1 h-16">
        {series.map((s) => {
          const isEmpty = s.impressions === 0;
          const heightPct = isEmpty ? 6 : Math.max(10, Math.round((s.impressions / max) * 100));
          return (
            <div key={s.iso} className="flex-1 flex flex-col items-center gap-1 group relative h-full">
              <div className="w-full flex-1 flex items-end">
                <div
                  className={
                    isEmpty
                      ? 'w-full rounded-t bg-fuchsia-500/15 border-t border-fuchsia-500/30'
                      : 'w-full rounded-t bg-gradient-to-t from-fuchsia-500/80 to-indigo-500/80 hover:from-fuchsia-400 hover:to-indigo-400 transition-colors'
                  }
                  style={{ height: `${heightPct}%` }}
                />
              </div>
              <span className="text-[9px] text-muted-foreground truncate w-full text-center leading-tight">
                {s.weekday}
              </span>
              <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-foreground text-background text-[10px] px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap shadow-sm">
                {s.impressions} {s.impressions === 1 ? 'impression' : 'impressions'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
